import React from "react";
import { useSearchParams, Link } from "react-router-dom";
import { CheckCircle2, Heart, ArrowRight } from "lucide-react";
import { PageHeader } from "@/components/PageHeader";
import { CtaButton } from "@/components/CtaButton";

export default function IntakeSuccessPage() {
    const [params] = useSearchParams();
    const id = params.get("id");
    const isStandaloneHost =
        typeof window !== "undefined" &&
        (window.location.hostname.startsWith("intake.") ||
            new URLSearchParams(window.location.search).get("intake") === "1");
    const mainSite =
        typeof window !== "undefined" && window.location.hostname.startsWith("intake.")
            ? `https://${window.location.hostname.replace(/^intake\./, "")}`
            : "/";
    const breadcrumbs = isStandaloneHost
        ? []
        : [
              { label: "Home", to: "/" },
              { label: "Intake", to: "/intake" },
              { label: "Received" },
          ];

    return (
        <div>
            <PageHeader
                eyebrow="INTAKE RECEIVED"
                title="Thank you for trusting us"
                subtitle="Your intake has been received. Riley personally reviews every submission and will reach out within 1–2 business days to schedule your consultation."
                breadcrumbs={breadcrumbs}
            />
            <section className="py-10">
                <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8">
                    <div className="rounded-3xl border border-border/80 bg-white/90 p-8 sm:p-12 text-center">
                        <span className="inline-flex h-14 w-14 items-center justify-center rounded-full bg-sage/20 text-moss">
                            <CheckCircle2 className="h-7 w-7" strokeWidth={1.5} />
                        </span>
                        <h2 className="mt-5 font-display text-3xl text-moss">
                            You’re all set.
                        </h2>
                        <p className="mt-3 text-foreground/75 leading-relaxed max-w-prose mx-auto">
                            Take a deep breath. The hardest part — telling your story — is done. While you wait for Riley to reach out, here are a few gentle ways to begin your Ayurvedic journey today.
                        </p>
                        {id && (
                            <p className="mt-3 text-xs text-foreground/50">
                                Reference ID: <span className="font-mono">{id}</span>
                            </p>
                        )}
                        <div className="mt-7 grid grid-cols-1 sm:grid-cols-3 gap-3 text-left">
                            {[
                                {
                                    title: "Read the journal",
                                    desc: "Reflections on Ayurveda and daily practice.",
                                    to: "/blog",
                                },
                                {
                                    title: "Learn the doshas",
                                    desc: "Discover Vata, Pitta, and Kapha.",
                                    to: "/why-ayurveda",
                                },
                                {
                                    title: "Browse treatments",
                                    desc: "See what may complement your plan.",
                                    to: "/treatments",
                                },
                            ].map((c) => {
                                const href =
                                    typeof window !== "undefined" &&
                                    window.location.hostname.startsWith("intake.")
                                        ? `https://${window.location.hostname.replace(
                                              /^intake\./,
                                              "",
                                          )}${c.to}`
                                        : c.to;
                                const isExternal = href.startsWith("http");
                                return isExternal ? (
                                    <a
                                        key={c.to}
                                        href={href}
                                        target="_blank"
                                        rel="noreferrer"
                                        className="rounded-2xl border border-border/80 bg-sand/40 p-4 hover:border-moss/40 transition-colors"
                                    >
                                        <p className="font-display text-lg text-moss">
                                            {c.title}
                                        </p>
                                        <p className="mt-1 text-sm text-foreground/70">
                                            {c.desc}
                                        </p>
                                        <span className="mt-2 inline-flex items-center gap-1 text-xs text-moss">
                                            Open <ArrowRight className="h-3 w-3" />
                                        </span>
                                    </a>
                                ) : (
                                    <Link
                                        key={c.to}
                                        to={c.to}
                                        className="rounded-2xl border border-border/80 bg-sand/40 p-4 hover:border-moss/40 transition-colors"
                                    >
                                        <p className="font-display text-lg text-moss">
                                            {c.title}
                                        </p>
                                        <p className="mt-1 text-sm text-foreground/70">
                                            {c.desc}
                                        </p>
                                        <span className="mt-2 inline-flex items-center gap-1 text-xs text-moss">
                                            Open <ArrowRight className="h-3 w-3" />
                                        </span>
                                    </Link>
                                );
                            })}
                        </div>
                        <div className="mt-8 inline-flex items-center gap-2 text-sm text-foreground/65">
                            <Heart className="h-4 w-4 text-terracotta" />
                            With care, Riley & the Choice Ayurveda team
                        </div>
                        <div className="mt-7">
                            {isStandaloneHost && mainSite.startsWith("http") ? (
                                <CtaButton href={mainSite} variant="secondary">
                                    Visit choiceayurveda.com
                                </CtaButton>
                            ) : (
                                <CtaButton to="/" variant="secondary">
                                    Back to home
                                </CtaButton>
                            )}
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
}
